import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { DialogFooter } from "@/components/ui/dialog";
import { Account } from "@accountHooks";
import { format } from "date-fns";
import { getDisplayName, disambiguateDisplayNames } from "@/lib/display-name";
import { useCurrencyList } from "@/hooks/useCurrencyList";

const securitiesTransactionFormSchema = z.object({
  transaction_type: z.enum(["deposit", "withdrawal"]),
  security_type: z.enum(["equity", "debt", "derivative"]),
  security_id: z.string().min(1, "Security account is required"),
  account_id: z.string().min(1, "Bank account is required"),
  amount: z.number().positive("Amount must be greater than 0"),
  currency: z.string().min(1, "Currency is required"),
  transaction_date: z.string().min(1, "Transaction date is required"),
  notes: z.string().max(500, "Notes must be 500 characters or less").optional(),
});

export type SecuritiesTransactionFormData = z.infer<
  typeof securitiesTransactionFormSchema
>;

interface SecurityOption {
  id: string;
  label: string;
  currency: string;
}

interface SecuritiesTransactionFormProps {
  transactionType: "deposit" | "withdrawal";
  securityType: "equity" | "debt" | "derivative";
  securities: SecurityOption[];
  accounts: Account[];
  onSave: (data: SecuritiesTransactionFormData) => void | Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
  defaultSecurityId?: string;
}

/**
 * SecuritiesTransactionForm component for creating securities transactions (deposits and withdrawals).
 * Handles security account selection, bank account selection, and transaction details.
 *
 * @param transactionType - Type of transaction: 'deposit' or 'withdrawal'
 * @param securityType - Type of security: 'equity', 'debt', or 'derivative'
 * @param securities - List of available securities for the given type
 * @param accounts - List of available bank accounts
 * @param onSave - Callback function when form is submitted with valid data
 * @param onCancel - Callback function when form is cancelled
 * @param isSubmitting - External submission state indicator
 * @param defaultSecurityId - Optional pre-selected security ID
 *
 * @example
 * <SecuritiesTransactionForm
 *   transactionType="deposit"
 *   securityType="equity"
 *   securities={equitySecurities}
 *   accounts={accounts}
 *   onSave={handleSave}
 *   onCancel={handleCancel}
 * />
 */
export function SecuritiesTransactionForm({
  transactionType: initialTransactionType,
  securityType,
  securities,
  accounts,
  onSave,
  onCancel,
  isSubmitting = false,
  defaultSecurityId,
}: SecuritiesTransactionFormProps) {
  // Find the default security to get its currency
  const defaultSecurity = defaultSecurityId
    ? securities.find((sec) => sec.id === defaultSecurityId)
    : undefined;

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting: formIsSubmitting },
  } = useForm<SecuritiesTransactionFormData>({
    resolver: zodResolver(securitiesTransactionFormSchema),
    defaultValues: {
      transaction_type: initialTransactionType,
      security_type: securityType,
      security_id: defaultSecurityId || "",
      account_id: "",
      amount: 0,
      currency: defaultSecurity?.currency || "USD",
      transaction_date: format(new Date(), "yyyy-MM-dd"),
      notes: "",
    },
  });
  const {
    data: availableCurrencies,
    isLoading: isLoadingCurrencies,
    error: errorLoadingCurrencies,
  } = useCurrencyList();

  const transactionTypeValue = watch("transaction_type");
  const securityId = watch("security_id");
  const currency = watch("currency");
  const accountId = watch("account_id");

  // Find selected security
  const selectedSecurity = securities.find((sec) => sec.id === securityId);

  // Find selected bank account
  const selectedAccount = accounts.find((acc) => acc.id === accountId);

  // Disambiguated account labels for the select
  const disambiguatedAccounts = disambiguateDisplayNames(
    accounts.map((a) => ({ ...a, displayName: getDisplayName(a) })),
  );

  // Update currency when security changes
  const handleSecurityChange = (value: string) => {
    setValue("security_id", value);
    const security = securities.find((sec) => sec.id === value);
    if (security) {
      setValue("currency", security.currency);
    }
  };

  const onSubmit = async (data: SecuritiesTransactionFormData) => {
    await onSave(data);
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="space-y-4"
      aria-label={`${transactionTypeValue === "deposit" ? "Deposit" : "Withdrawal"} transaction form`}>
      <div className="space-y-2">
        <Label htmlFor="transaction-type-select">Transaction Type</Label>
        <Select
          value={transactionTypeValue}
          onValueChange={(value) => {
            setValue("transaction_type", value as "deposit" | "withdrawal");
          }}>
          <SelectTrigger
            id="transaction-type-select"
            aria-label="Select transaction type"
            autoFocus>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="deposit">Deposit</SelectItem>
            <SelectItem value="withdrawal">Withdrawal</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="security_id">Security Account *</Label>
        <Select value={securityId} onValueChange={handleSecurityChange}>
          <SelectTrigger
            id="security_id"
            aria-label="Select security account"
            aria-required="true"
            aria-describedby={
              errors.security_id ? "security-id-error" : undefined
            }
            aria-invalid={!!errors.security_id}>
            <SelectValue placeholder="Select security account" />
          </SelectTrigger>
          <SelectContent>
            {securities.map((sec) => (
              <SelectItem key={sec.id} value={sec.id}>
                {sec.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.security_id && (
          <p
            id="security-id-error"
            className="text-sm text-destructive"
            role="alert">
            {errors.security_id.message}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="account_id">
          {transactionTypeValue === "deposit"
            ? "Source Bank Account *"
            : "Destination Bank Account *"}
        </Label>
        <Select
          value={accountId}
          onValueChange={(value) => setValue("account_id", value)}>
          <SelectTrigger
            id="account_id"
            aria-label="Select bank account"
            aria-required="true"
            aria-describedby={
              errors.account_id ? "account-id-error" : undefined
            }
            aria-invalid={!!errors.account_id}>
            <SelectValue placeholder="Select bank account" />
          </SelectTrigger>
          <SelectContent>
            {disambiguatedAccounts.map((acc) => (
              <SelectItem key={acc.id} value={acc.id}>
                {acc.displayName} ({acc.currency})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.account_id && (
          <p
            id="account-id-error"
            className="text-sm text-destructive"
            role="alert">
            {errors.account_id.message}
          </p>
        )}
        {selectedAccount && (
          <p className="text-xs text-muted-foreground">
            Current balance:{" "}
            {selectedAccount.balance.toLocaleString(undefined, {
              style: "currency",
              currency: selectedAccount.currency,
            })}
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="amount">Amount *</Label>
          <Input
            id="amount"
            type="number"
            step="0.01"
            min="0.01"
            {...register("amount", { valueAsNumber: true })}
            placeholder="e.g. 1000"
            aria-label="Transaction amount"
            aria-required="true"
            aria-describedby={errors.amount ? "amount-error" : undefined}
            aria-invalid={!!errors.amount}
          />
          {errors.amount && (
            <p
              id="amount-error"
              className="text-sm text-destructive"
              role="alert">
              {errors.amount.message}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="transaction_date">Date *</Label>
          <Input
            id="transaction_date"
            type="date"
            {...register("transaction_date")}
            aria-label="Transaction date"
            aria-required="true"
            aria-describedby={
              errors.transaction_date ? "transaction-date-error" : undefined
            }
            aria-invalid={!!errors.transaction_date}
          />
          {errors.transaction_date && (
            <p
              id="transaction-date-error"
              className="text-sm text-destructive"
              role="alert">
              {errors.transaction_date.message}
            </p>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="currency">Currency *</Label>
        <Select
          value={currency}
          onValueChange={(value) => setValue("currency", value)}>
          <SelectTrigger
            id="currency"
            aria-label="Select currency"
            aria-required="true">
            <SelectValue />
          </SelectTrigger>
          {isLoadingCurrencies && "Loading Currencies"}
          {!isLoadingCurrencies && !errorLoadingCurrencies ? (
            <SelectContent>
              {availableCurrencies.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          ) : (
            "Failed to load currencies"
          )}
        </Select>
        {errors.currency && (
          <p className="text-sm text-destructive" role="alert">
            {errors.currency.message}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="notes">Notes</Label>
        <Textarea
          id="notes"
          {...register("notes")}
          placeholder="Additional notes..."
          rows={2}
          maxLength={500}
          aria-label="Transaction notes"
        />
        {errors.notes && (
          <p className="text-sm text-destructive" role="alert">
            {errors.notes.message}
          </p>
        )}
      </div>

      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isSubmitting || formIsSubmitting}
          aria-label="Cancel transaction">
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isSubmitting || formIsSubmitting}
          aria-label={`${transactionTypeValue === "deposit" ? "Deposit" : "Withdraw"} funds`}>
          {isSubmitting || formIsSubmitting
            ? "Processing..."
            : transactionTypeValue === "deposit"
              ? "Deposit"
              : "Withdraw"}
        </Button>
      </DialogFooter>
    </form>
  );
}
